import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { generalStore, userStore } from '../../store';
import debounce from 'debounce';
import { isEmpty } from 'ramda';

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RESPONSE, ROUTES } from '../../js/_constants';
import Helpers, { sanitizeCollectionResponse } from '../../js/helpers';
import Loader from '../../components/loader/Loader';
import GoBackButton from '../../components/goBackButton/GoBackButton';
import InputField from '../../components/inputField/InputField';
import Pagination from '../../components/pagination/Pagination';

import api from '../../api';
import './DealershipDevicesView.scss';

const DealershipDevicesView: React.FC = () => {
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const { setPageName } = generalStore();
    const { t } = useTranslation();
    const { details } = userStore();
    const controller = new AbortController();
    const signal = controller.signal;
    const [loading, setLoading] = useState(false);
    const [deviceList, setDeviceList] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPages] = useState(1);
    const [deviceQuery, setDeviceQuery] = useState('');

    const getDealer = async () => {
        if (!id) {
            return;
        }

        const dealershipResponse = await api.devices.getDealer(id);
        setPageName(`${t('devices_for')} ${dealershipResponse.title}`);
    }

    const getDevice = async (page, query, force_update=false) => {
        setLoading(true);
        const devicesResponse = await api.devices.getDevices({
            params: {
                brand: details.userGrantedBrands,
                ...(force_update && {
                    force_update: 1
                }),
                page,
                filterKvps: id,
                ...(query && {
                    deviceIdentifier: query,
                    title: query
                })
            },
            signal
        });
        const sanitizeResponse = sanitizeCollectionResponse(devicesResponse);
        setTotalPages(sanitizeResponse.totalPages);
        setCurrentPages(sanitizeResponse.currentPage);
        setDeviceList(devicesResponse[RESPONSE.EXPAND_COLLECTION]);
        setLoading(false);
    };

    const handlePaginationClick = async (pageNumber: number) => {
        getDevice(pageNumber, deviceQuery);
    };

    useEffect(() => {
        return () => {
            setPageName('');
        };
    }, []);

    useEffect(() => {
        const currentSearch = Helpers.getQueryParameter();
        getDealer();
        getDevice(currentSearch.page, '', true);
    }, [location, id]);

    const onDeviceQueryChange = value => {
        setDeviceQuery(value.target.value);
        getDevice(1, value.target.value);
    };

    const onClearValue = () => {
        setDeviceQuery('');
        getDevice(1, '');
    };

    const debouncedDeviceQueryChange = debounce(onDeviceQueryChange, 1000);
    
    const renderDevice = (device, index) => {
        let playlistString = t('device_no_playlist_assigned');

        if (device.playlists.length > 0) {
            playlistString = `${device.playlists
                .sort(Helpers.sortAlphabetically)
                .map(playlist => ` ${playlist.title}`)}.`;
        }

        return (
            <Link
                className="devices-list-item"
                key={device[RESPONSE.ID]}
                to={`${ROUTES.DEALERSHIPS}${ROUTES.DEVICES}/${device.deviceNumber}?brand=${device.brand.split('/').pop()}`}
                data-testid={`device-card-${index + 1}`}
            >
                <div>
                    <p className="device-title" data-testid={`device-title-${index + 1}`}>{device.title} {Helpers.isRecentlyAdded(device.createdAt) && <span className='recently-added'>{t('recently_added')}</span>}</p>
                    <p className='device-number'>
                        {t('device_identifiers')}: {device.deviceNumber && <span className="device-id">{device.deviceNumber}</span>}{device.serialNumber && <span className="device-id">{device.serialNumber}</span>}{device.thingName && <span className="device-id">{device.thingName}</span>}
                    </p>
                    <p className="device-playlists-names">
                        {device.playlists.length <= 1
                            ? t('device_playlist')
                            : t('device_playlists')}
                        : {playlistString}
                    </p>
                </div>
                <span className='arrow-back'></span>
            </Link>
        );
    };

    const renderPagination = () => {
        if (totalPages > 1 && !loading) {
            return (
                <Pagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onClick={handlePaginationClick}
                />
            );
        }
    }

    return (
        <div className="b-dealership-devices">
            <div className='back-button-wrapper'>
                <GoBackButton />
                <InputField
                    clearIcon
                    hideLabel={false}
                    name="divices-filter"
                    label={t('filter_devices')}
                    onChangeHandler={debouncedDeviceQueryChange}
                    onClearHandler={onClearValue}
                />
            </div>
            <div className="inner-content">
                {loading ? (
                    <Loader loading />
                ) : (
                    isEmpty(deviceList) 
                        ? (<span className="b-dealership-devices__empty-state-message">
                            {t('devices_view_no_search_results')}
                            </span>)
                        :deviceList.map((device, i) => renderDevice(device, i))
                )}
            </div>
            {renderPagination()}
        </div>
    );
};
export default DealershipDevicesView;